import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  BillingInformation,
  Session,
  useCreditBalance,
  usePlan,
} from '@app/api';

import { useTranslation } from 'react-i18next';
import { toEuros } from 'i18n';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase,
    paddingTop: theme.marginBase * 3,
  },
  infoBlock: {},
  infoTitle: {
    ...theme.fonts.h5,
  },
  checkList: {
    marginTop: theme.marginBase,
  },
  check: {
    ...theme.fonts.caption,
    color: theme.colors.inkTertiary,
  },
  amount: {
    ...theme.fonts.caption,
  },
  checkKO: {
    color: theme.colors.redAccessible,
  },
  instructions: {
    ...theme.fonts.caption,
    whiteSpace: 'pre-line',
    backgroundColor: theme.colors.white,
    padding: theme.marginBase,
    borderRadius: theme.borderRadius.std,
    marginTop: theme.marginBase,
  },
}));

interface Props {
  billingInformation: BillingInformation;
  session: Session;
}

export const InvoicingModalPane = ({ session, billingInformation }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: balance } = useCreditBalance(session.organizationId);
  const { data: plan } = usePlan(session.planId);

  return (
    <div className={classes.container}>
      {plan && (
        <div className={classes.infoBlock}>
          <h5 className={classes.infoTitle}>
            {t(`billing.billingMethod.label`)} :{' '}
            <span className={classes.amount}>
              {t(`billing.billingMethod.name.${plan.billingMethod}`)}
            </span>
          </h5>
        </div>
      )}

      {balance && (
        <div className={classes.infoBlock}>
          <h5 className={classes.infoTitle}>
            {t('admin.billing.invoicing.pane.remainingCredits')} :{' '}
            <span className={classes.amount}>{toEuros(balance.amount)}</span>
          </h5>
        </div>
      )}

      <div className={classes.infoBlock}>
        <h5 className={classes.infoTitle}>
          {t('admin.billing.invoicing.pane.attentionPoints')}
        </h5>
        <div className={classes.checkList}>
          <div
            className={classNames(classes.check, {
              [classes.checkKO]: billingInformation.preventAutomaticBilling,
            })}
          >
            {billingInformation.preventAutomaticBilling
              ? t(
                  'admin.billing.billingInfo.finalVerification.preventAutomaticBilling',
                )
              : t('admin.billing.invoicing.pane.simpleBilling')}
          </div>

          <div
            className={classNames(classes.check, {
              [classes.checkKO]: billingInformation.requirePurchaseOrderNumber,
            })}
          >
            {billingInformation.requirePurchaseOrderNumber
              ? t(
                  'admin.billing.billingInfo.finalVerification.requirePurchaseOrderNumber',
                )
              : t('admin.billing.invoicing.pane.noPurchaseOrderRequired')}
          </div>

          <div
            className={classNames(classes.check, {
              [classes.checkKO]:
                billingInformation.requireUseOfSpecificPlatform,
            })}
          >
            {billingInformation.requireUseOfSpecificPlatform
              ? t(
                  'admin.billing.billingInfo.finalVerification.requireUseOfSpecificPlatform',
                )
              : t('admin.billing.invoicing.pane.noSpecificPlatform')}
          </div>
        </div>
      </div>

      {session.billingNote && (
        <div className={classes.infoBlock}>
          <h5 className={classes.infoTitle}>
            {t('admin.billing.invoicing.pane.specificInstructions')}
          </h5>
          <p className={classes.instructions}>{session.billingNote}</p>
        </div>
      )}

      {billingInformation.billingInstructions && (
        <div className={classes.infoBlock}>
          <h5 className={classes.infoTitle}>
            {t('admin.billing.invoicing.pane.generalInstructions')}
          </h5>
          <p className={classes.instructions}>
            {billingInformation.billingInstructions}
          </p>
        </div>
      )}
    </div>
  );
};
